import React, { useEffect, useState } from 'react';
import { Switch, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

import PageHeading from 'allGlobalComponents/PageHeading';
import SnackbarOmni from 'allGlobalComponents/SnackbarOmni';
import TableOmniNewAnimated from 'allGlobalComponents/TableOmniNewAnimated';
import DateFilterWithExport from 'allGlobalComponents/DateFilterWithExport';
import ButtonOmni from 'allGlobalComponents/ButtonOmni';
import ButtonOmniNew from 'allGlobalComponents/ButtonOmniNew';
import ModalOmni from 'allGlobalComponents/ModalOmni';
import FormFieldOmni from 'allGlobalComponents/FormFieldOmni';
import { useSnackbar, useApi, usePagination } from 'app/hooks';
import BannerImageUploader from './BannerImageUploader';

const useStyles = makeStyles(theme => ({
	content: {
		width: '100%',
		maxWidth: '100%',
		minWidth: 0,
		boxSizing: 'border-box',
		padding: '0 16px'
	},
	formContent: {
		padding: theme.spacing(3),
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(2)
	},
	formRow: {
		display: 'flex',
		gap: theme.spacing(2),
		flexWrap: 'wrap',
		[theme.breakpoints.down('sm')]: {
			flexDirection: 'column'
		}
	},
	buttonContainer: {
		width: '100%',
		display: 'flex',
		justifyContent: 'flex-end',
		gap: theme.spacing(2)
	},
	thumb: {
		width: 56,
		height: 34,
		objectFit: 'cover',
		borderRadius: 4,
		border: `1px solid ${theme.palette.divider}`
	},
	switch: {
		'& .MuiSwitch-switchBase': {
			color: theme.palette.type === 'dark' ? '#bdbdbd' : undefined
		},
		'& .MuiSwitch-track': {
			backgroundColor: theme.palette.type === 'dark' ? '#757575' : undefined
		}
	}
}));

const INITIAL_FORM = {
	title: '',
	image: '',
	body_text: '',
	button_text: '',
	button_url: '',
	is_active: true
};

const STATUS_OPTIONS = [
	{ value: 'true', label: 'Active' },
	{ value: 'false', label: 'Inactive' }
];

// Grows the body-text textarea with its content (min 3 rows) — same behaviour as
// the admin chatbot "Agent Conversation Start Message" field.
const calcRows = val => {
	const text = val || '';
	const total = text.split('\n').reduce((sum, line) => sum + Math.max(1, Math.ceil(line.length / 50)), 0);
	return Math.max(3, total);
};

function DynamicBanner() {
	const classes = useStyles();

	// Hooks
	const { snackbar, showSuccess, showError, showWarning } = useSnackbar();
	const { currentParams, setCurrentParams } = usePagination(10);
	const { isLoading, request } = useApi();

	// State
	const [data, setData] = useState([]);
	const [createOpen, setCreateOpen] = useState(false);
	const [editOpen, setEditOpen] = useState(false);
	const [selectedRow, setSelectedRow] = useState(null);
	const [formData, setFormData] = useState(INITIAL_FORM);
	const [saving, setSaving] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [togglingId, setTogglingId] = useState(null);
	const [refreshKey, setRefreshKey] = useState(0);

	// Fetch banners
	const fetchBanners = () => {
		request({
			module: 'dynamicBanner',
			method: 'fetch',
			payload: { columns: '*', where: '1=1' },
			onSuccess: response => {
				const banners = response.data.data.banners || [];
				// Newest first
				setData([...banners].sort((a, b) => Number(b.id) - Number(a.id)));
			},
			onError: message => showError(message || 'Error fetching banners')
		});
	};

	useEffect(() => {
		fetchBanners();
	}, [refreshKey]);

	const handleRefresh = () => setRefreshKey(prev => prev + 1);

	const handleFormChange = (field, value) => {
		setFormData(prev => ({ ...prev, [field]: value }));
	};

	const resetForm = () => {
		setFormData(INITIAL_FORM);
	};

	const isFormInvalid = () => {
		return (
			!formData.title.trim() ||
			!formData.image.trim() ||
			!formData.body_text.trim() ||
			!formData.button_text.trim() ||
			!formData.button_url.trim() ||
			saving ||
			uploading
		);
	};

	// Create
	const handleCreateOpen = () => {
		resetForm();
		setCreateOpen(true);
	};

	const handleCreate = () => {
		if (isFormInvalid()) {
			showWarning('Please fill in all fields');
			return;
		}
		setSaving(true);
		request({
			module: 'dynamicBanner',
			method: 'create',
			payload: {
				title: formData.title,
				image: formData.image,
				body_text: formData.body_text,
				button_text: formData.button_text,
				button_url: formData.button_url,
				is_active: !!formData.is_active
			},
			onSuccess: () => {
				showSuccess('Banner created successfully');
				setCreateOpen(false);
				resetForm();
				fetchBanners();
			},
			onError: message => showError(message || 'Error creating banner'),
			onFinally: () => setSaving(false)
		});
	};

	// Edit
	const handleEditOpen = row => {
		setSelectedRow(row);
		setFormData({
			title: row.title || '',
			image: row.image || '',
			body_text: row.body_text || '',
			button_text: row.button_text || '',
			button_url: row.button_url || '',
			is_active: !!row.is_active
		});
		setEditOpen(true);
	};

	const handleUpdate = () => {
		if (isFormInvalid()) {
			showWarning('Please fill in all fields');
			return;
		}
		setSaving(true);
		request({
			module: 'dynamicBanner',
			method: 'update',
			payload: {
				id: selectedRow.id,
				title: formData.title,
				image: formData.image,
				body_text: formData.body_text,
				button_text: formData.button_text,
				button_url: formData.button_url,
				is_active: !!formData.is_active
			},
			onSuccess: () => {
				showSuccess('Banner updated successfully');
				setEditOpen(false);
				resetForm();
				setSelectedRow(null);
				fetchBanners();
			},
			onError: message => showError(message || 'Error updating banner'),
			onFinally: () => setSaving(false)
		});
	};

	// Toggle active status
	const handleToggle = row => {
		setTogglingId(row.id);
		request({
			module: 'dynamicBanner',
			method: 'toggle',
			payload: { id: Number(row.id), is_active: !row.is_active },
			onSuccess: () => {
				showSuccess(`Banner ${!row.is_active ? 'activated' : 'deactivated'} successfully`);
				fetchBanners();
			},
			onError: message => showError(message || 'Error toggling banner'),
			onFinally: () => setTogglingId(null)
		});
	};

	// Table columns
	const headCells = [
		{ id: 'id', numeric: false, disablePadding: false, label: 'ID' },
		{
			id: 'image',
			numeric: false,
			disablePadding: false,
			label: 'Image',
			disableSort: true,
			customRow: row =>
				row.image ? (
					<img
						src={row.image}
						alt={row.title || 'banner'}
						className={classes.thumb}
						onError={e => {
							e.target.style.display = 'none';
						}}
					/>
				) : (
					'-'
				)
		},
		{ id: 'title', numeric: false, disablePadding: false, label: 'Title', tooltip: true },
		{ id: 'body_text', numeric: false, disablePadding: false, label: 'Body Text', msg: true, truncateLength: 40 },
		{ id: 'button_text', numeric: false, disablePadding: false, label: 'Button Text', tooltip: true },
		{ id: 'button_url', numeric: false, disablePadding: false, label: 'Button URL', msg: true, truncateLength: 40 },
		{
			id: 'is_active',
			numeric: false,
			disablePadding: false,
			label: 'Status',
			disableSort: true,
			pill: true,
			pillConfig: {
				mapping: {
					true: {
						label: 'Active',
						color: '#065F46',
						bgColor: '#D1FAE5',
						icon: <CheckCircleIcon style={{ fontSize: 16 }} />
					},
					false: {
						label: 'Inactive',
						color: '#991B1B',
						bgColor: '#FEE2E2',
						icon: <CancelIcon style={{ fontSize: 16 }} />
					}
				},
				defaultColor: '#9e9e9e',
				defaultBgColor: 'rgba(158, 158, 158, 0.1)'
			}
		},
		{ id: 'dt', numeric: false, disablePadding: false, label: 'Created', dt: true },
		{ id: 'dtu', numeric: false, disablePadding: false, label: 'Updated', dt: true },
		{
			id: 'toggle',
			numeric: false,
			disablePadding: false,
			label: 'Active',
			disableSort: true,
			customRow: row => (
				<Switch
					checked={row.is_active === true}
					onChange={() => handleToggle(row)}
					disabled={togglingId === row.id}
					className={classes.switch}
					size="small"
				/>
			)
		},
		{ id: 'edit', numeric: false, disablePadding: false, label: 'Edit' }
	];

	// Create button
	const createButton = (
		<ButtonOmniNew buttonType="create" label="Create Banner" onClick={handleCreateOpen} variant="outlined" />
	);

	// Shared form
	const renderFormContent = () => (
		<div className={classes.formContent}>
			<FormFieldOmni
				label="Title"
				variant="outlined"
				value={formData.title}
				onChange={e => handleFormChange('title', e.target.value)}
				size="small"
				fullWidth
				placeholder="e.g. New Feature Launch"
			/>

			{/* Image uploader (drag & drop) */}
			<BannerImageUploader
				value={formData.image}
				onChange={url => handleFormChange('image', url)}
				onUploadingChange={setUploading}
			/>

			<TextField
				label="Body Text"
				variant="outlined"
				value={formData.body_text}
				onChange={e => handleFormChange('body_text', e.target.value)}
				fullWidth
				multiline
				rows={calcRows(formData.body_text)}
				placeholder="e.g. Our new feature is here!!"
			/>
			<div className={classes.formRow}>
				<FormFieldOmni
					label="Button Text"
					variant="outlined"
					value={formData.button_text}
					onChange={e => handleFormChange('button_text', e.target.value)}
					size="small"
					fullWidth
					placeholder="e.g. Learn More"
				/>
				<FormFieldOmni
					label="Status"
					variant="outlined"
					value={String(formData.is_active)}
					onChange={e => handleFormChange('is_active', e.target.value === 'true')}
					type="select"
					options={STATUS_OPTIONS}
					size="small"
					fullWidth
				/>
			</div>
			<FormFieldOmni
				label="Button URL"
				variant="outlined"
				value={formData.button_url}
				onChange={e => handleFormChange('button_url', e.target.value)}
				size="small"
				fullWidth
				placeholder="https://example.com/contact/"
			/>
		</div>
	);

	const createActions = (
		<div className={classes.buttonContainer}>
			<ButtonOmni
				onClick={() => {
					setCreateOpen(false);
					resetForm();
				}}
				variant="outlined"
				label="Cancel"
				disabled={saving}
			/>
			<ButtonOmni
				onClick={handleCreate}
				variant="outlined"
				label={saving ? 'Creating...' : 'Create'}
				disabled={isFormInvalid()}
			/>
		</div>
	);

	const editActions = (
		<div className={classes.buttonContainer}>
			<ButtonOmni
				onClick={() => {
					setEditOpen(false);
					resetForm();
					setSelectedRow(null);
				}}
				variant="outlined"
				label="Cancel"
				disabled={saving}
			/>
			<ButtonOmni
				onClick={handleUpdate}
				variant="outlined"
				label={saving ? 'Updating...' : 'Update'}
				disabled={isFormInvalid()}
			/>
		</div>
	);

	return (
		<div className={classes.content}>
			<PageHeading icon={ViewCarouselIcon} title="Dynamic Banner" />

			<DateFilterWithExport
				showDateRange={false}
				showExport={false}
				filterFields={[]}
				filterValues={{}}
				onFiltersApply={() => {}}
				onFiltersClear={() => {}}
				showRefresh
				onRefresh={handleRefresh}
				customRightContent={createButton}
			/>

			<TableOmniNewAnimated
				rows={data}
				headCells={headCells}
				perPageRows={currentParams.limit}
				setCurrentParams={setCurrentParams}
				curPage={currentParams.page}
				loading={isLoading}
				onEditClick={handleEditOpen}
				fixedHeight
				maxTableHeight="600px"
				rowsPerPageOptions={[5, 10, 25, 50]}
				emptyMessage="No Banners Found"
			/>

			{/* Create Dialog */}
			<ModalOmni
				disableBackdropClick
				open={createOpen}
				handleClose={() => {
					setCreateOpen(false);
					resetForm();
				}}
				title="Create Banner"
				content={renderFormContent()}
				actions={createActions}
				customWidth={560}
			/>

			{/* Edit Dialog */}
			<ModalOmni
				disableBackdropClick
				open={editOpen}
				handleClose={() => {
					setEditOpen(false);
					resetForm();
					setSelectedRow(null);
				}}
				title="Edit Banner"
				content={renderFormContent()}
				actions={editActions}
				customWidth={560}
			/>

			<SnackbarOmni {...snackbar} />
		</div>
	);
}

export default React.memo(DynamicBanner);
